/**
 * Horse Request Widget Component
 * Allows players to request a horse when signing up for an event
 */
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { Horse, HorseRequest, HorseAssignment } from "./types";
import HorseIcon from "./HorseIcon";

interface HorseRequestWidgetProps {
  devPort: string;
  clubId: number;
  eventType: "games" | "lessons" | "tournaments";
  eventId: string | number;
  playerId: string | number;
  maxChukkers?: number; // For games/tournaments
  managerMode: number;
  onChange?: (requested: boolean, chukkers: number) => void;
}

function HorseRequestWidget({
  devPort,
  clubId,
  eventType,
  eventId,
  playerId,
  maxChukkers = 4,
  managerMode,
  onChange,
}: HorseRequestWidgetProps) {
  const [needsHorse, setNeedsHorse] = useState(false);
  const [chukkersRequested, setChukkersRequested] = useState(1);
  const [existingRequest, setExistingRequest] = useState<HorseRequest | null>(null);
  const [assignedHorses, setAssignedHorses] = useState<HorseAssignment[]>([]);
  const [horses, setHorses] = useState<Horse[]>([]);
  const [loading, setLoading] = useState(true);

  const isLesson = eventType === "lessons";

  // Load existing request and assignments
  const loadData = useCallback(() => {
    if (!playerId || playerId === -1) return;
    
    setLoading(true);

    Promise.all([
      fetch(
        `${devPort}horses/player-request/${clubId}/${eventType}/${eventId}/${playerId}`
      ).then((r) => r.json()),
      fetch(
        `${devPort}horses/player-assignments/${clubId}/${eventType}/${eventId}/${playerId}`
      ).then((r) => r.json()),
      fetch(`${devPort}horses/available/${clubId}`).then((r) => r.json()),
    ])
      .then(([request, assignments, availableHorses]) => {
        if (request && request.id) {
          setExistingRequest(request);
          setNeedsHorse(true);
          setChukkersRequested(request.chukkers_requested || 1);
        }
        setAssignedHorses(Array.isArray(assignments) ? assignments : []);
        setHorses(Array.isArray(availableHorses) ? availableHorses : []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [devPort, clubId, eventType, eventId, playerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Submit/update horse request
  const handleRequestHorse = () => {
    fetch(devPort + "horses/request", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        club_id: clubId,
        event_type: eventType,
        event_id: eventId,
        player_id: playerId,
        chukkers_requested: isLesson ? 1 : chukkersRequested,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.id) {
          setExistingRequest(data);
        }
        if (onChange) {
          onChange(true, isLesson ? 1 : chukkersRequested);
        }
      })
      .catch(() => {});
  };

  // Cancel horse request
  const handleCancelRequest = () => {
    fetch(devPort + "horses/cancel-request", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        club_id: clubId,
        event_type: eventType,
        event_id: eventId,
        player_id: playerId,
      }),
    })
      .then(() => {
        setExistingRequest(null);
        setNeedsHorse(false);
        if (onChange) {
          onChange(false, 0);
        }
      })
      .catch(() => {});
  };

  const handleNeedsHorseChange = (checked: boolean) => {
    setNeedsHorse(checked);
    if (checked) {
      handleRequestHorse();
    } else {
      handleCancelRequest();
    }
  };

  const getHorseInfo = (horseId: string) => {
    return horses.find((h) => h.id === horseId);
  };

  if (loading || !playerId || playerId === -1) {
    return null;
  }

  // If manager assigned horses, show them
  if (assignedHorses.length > 0) {
    const totalChukkers = assignedHorses.reduce(
      (sum, a) => sum + a.chukkers_assigned,
      0
    );

    return (
      <Card variant="outlined" sx={{ bgcolor: "success.light", opacity: 0.9 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
              🐴 Horse{assignedHorses.length > 1 ? "s" : ""} Assigned
            </Typography>
            <List dense disablePadding>
              {assignedHorses.map((assignment) => {
                const horse = getHorseInfo(assignment.horse_id);
                return (
                  <ListItem key={assignment.id} disablePadding>
                    <ListItemAvatar>
                      <HorseIcon color={horse?.color || "Bay"} size={32} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={assignment.horse_name}
                      secondary={
                        isLesson
                          ? "Assigned for this lesson"
                          : `${assignment.chukkers_assigned} chukker${
                              assignment.chukkers_assigned > 1 ? "s" : ""
                            }`
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            {!isLesson && (
              <Typography variant="body2" color="success.dark">
                Total: {totalChukkers} chukker{totalChukkers > 1 ? "s" : ""} of horse time
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Show request form (always show checkbox so users can uncheck even if request exists)
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            🐴 Need a Horse?
          </Typography>

          {/* Show pending status if request exists */}
          {existingRequest && existingRequest.status === "pending" && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                <Typography variant="body2">
                  Your request is pending. The manager will assign a horse.
                </Typography>
                <Chip
                  label={
                    isLesson
                      ? "1 horse"
                      : `${existingRequest.chukkers_requested} chukker${
                          existingRequest.chukkers_requested > 1 ? "s" : ""
                        }`
                  }
                  size="small"
                  color="warning"
                />
              </Stack>
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={needsHorse}
                onChange={(e) => handleNeedsHorseChange(e.target.checked)}
              />
            }
            label={isLesson ? "I need a horse for this lesson" : "I need a horse"}
          />

          {needsHorse && !isLesson && (
            <Box sx={{ px: 2 }}>
              <Typography gutterBottom>
                Chukkers needed: {chukkersRequested}
              </Typography>
              <Slider
                value={chukkersRequested}
                onChange={(_, value) => {
                  setChukkersRequested(value as number);
                }}
                onChangeCommitted={() => {
                  // Update request when slider is released
                  if (existingRequest) {
                    handleRequestHorse();
                  }
                }}
                min={1}
                max={maxChukkers}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleRequestHorse}
                sx={{ mt: 1 }}
              >
                Update Request
              </Button>
            </Box>
          )}

          {!needsHorse && horses.length === 0 && (
            <Alert severity="info" variant="outlined">
              No horses are currently available at this club.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default HorseRequestWidget;

